import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Edit,
  Play,
  Clock,
  FileQuestion,
  Users,
  CheckCircle,
  AlertCircle,
  Archive,
  BarChart3,
  Send,
  Target,
  Shuffle,
  Shield,
  Share2,
  Copy,
  Link2,
  ExternalLink,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatDate } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-gray-500', icon: AlertCircle },
  published: { label: 'Publié', color: 'bg-green-500', icon: CheckCircle },
  archived: { label: 'Archivé', color: 'bg-orange-500', icon: Archive },
};

const typeLabels = {
  single_choice: 'QCM unique',
  multiple_choice: 'QCM multiple',
  true_false: 'Vrai/Faux',
  short_answer: 'Réponse courte',
  long_answer: 'Réponse longue',
  matching: 'Appariement',
  ordering: 'Classement',
};

export default function ExamDetailPage() {
  const { workspaceId, examId } = useParams();
  const navigate = useNavigate();
  const { user, canManage } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDomain, setShareDomain] = useState(null);
  
  useEffect(() => {
    const init = async () => {
      // Load sharing config to get custom domain
      try {
        const sharingRes = await fetch(`${API_URL}/api/sharing/config`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (sharingRes.ok) {
          const sharingData = await sharingRes.json();
          if (sharingData.share_domain) {
            setShareDomain(sharingData.share_domain);
          }
        }
      } catch (e) {
        console.log('Sharing config not available');
      }
      
      if (workspaceId && examId) {
        await loadExam(workspaceId);
      }
    };
    
    init();
  }, [workspaceId, examId]);

  // Redirect candidates to take page — they should not see exam details
  useEffect(() => {
    if (!loading && exam && !canManage && exam.status === 'published') {
      navigate(`/workspace/${workspaceId}/exams/${examId}/take`, { replace: true });
    }
  }, [loading, exam, canManage]);
  
  const loadExam = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/${examId}?workspace_id=${wsId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExam(data);
      } else {
        toast.error('Examen non trouvé');
        navigate(`/workspace/${workspaceId}/exams`);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePublish = async () => {
    try {
      const response = await fetch(`${API_URL}/api/exams/${examId}/publish?workspace_id=${workspaceId}`, {
        method: 'POST',
      });
      if (response.ok) {
        toast.success('Examen publié');
        loadExam(workspaceId);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la publication');
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };
  
  const handleArchive = async () => {
    try {
      await fetch(`${API_URL}/api/exams/${examId}/archive?workspace_id=${workspaceId}`, {
        method: 'POST',
      });
      toast.success('Examen archivé');
      loadExam(workspaceId);
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const getShareUrl = () => {
    // Use custom share domain if configured, otherwise fallback to current origin
    let baseUrl = window.location.origin;
    if (shareDomain) {
      // Ensure the domain has a protocol
      baseUrl = shareDomain.startsWith('http') ? shareDomain : `https://${shareDomain}`;
    }
    return `${baseUrl}/exam/${exam?.slug || examId}/register`;
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      toast.success('Lien copié !');
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };
  
  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </WorkspaceLayout>
    );
  }
  
  if (!exam) {
    return (
      <WorkspaceLayout>
        <div className="p-6 text-center">
          <h1 className="text-2xl font-bold mb-4">Examen non trouvé</h1>
          <Button onClick={() => navigate(`/workspace/${workspaceId}/exams`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux examens
          </Button>
        </div>
      </WorkspaceLayout>
    );
  }
  
  const status = statusConfig[exam.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  const totalPoints = exam.questions?.reduce((sum, q) => sum + (q.points || 1), 0) || 0;
  
  return (
    <WorkspaceLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/exams`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold">{exam.title}</h1>
                <Badge className={`${status.color} text-white`}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {status.label}
                </Badge>
              </div>
              {exam.description && (
                <p className="text-muted-foreground">{exam.description}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {exam.category && <Badge variant="outline">{exam.category}</Badge>}
                {exam.certification && <Badge variant="secondary">{exam.certification}</Badge>}
                {exam.level && <Badge variant="secondary">{exam.level}</Badge>}
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            {exam.status === 'published' && (
              <>
                <Button onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}/take`)}>
                  <Play className="h-4 w-4 mr-2" />
                  Passer l'examen
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShareDialogOpen(true)}
                  className="text-green-600 border-green-300 hover:bg-green-50"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Partager
                </Button>
              </>
            )}
            {canManage && (
              <>
                <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}/edit`)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Modifier
                </Button>
                {exam.status === 'draft' && (
                  <Button onClick={handlePublish}>
                    <Send className="h-4 w-4 mr-2" />
                    Publier
                  </Button>
                )}
                {exam.status === 'published' && (
                  <Button variant="outline" onClick={handleArchive}>
                    <Archive className="h-4 w-4 mr-2" />
                    Archiver
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <FileQuestion className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Questions</p>
                  <p className="text-2xl font-bold">{exam.question_count || exam.questions?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Clock className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Durée</p>
                  <p className="text-2xl font-bold">{exam.settings?.duration_minutes || 60} min</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Score requis</p>
                  <p className="text-2xl font-bold">{exam.settings?.passing_score || 50}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total points</p>
                  <p className="text-2xl font-bold">{totalPoints}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Settings Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Paramètres de l'examen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Tentatives maximum</span>
                  <span className="font-medium">{exam.settings?.max_attempts || 1}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Mélanger les questions</span>
                  <Badge variant={exam.settings?.randomize_questions ? "default" : "secondary"}>
                    {exam.settings?.randomize_questions ? "Oui" : "Non"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Mélanger les choix</span>
                  <Badge variant={exam.settings?.randomize_choices ? "default" : "secondary"}>
                    {exam.settings?.randomize_choices ? "Oui" : "Non"}
                  </Badge>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Résultats immédiats</span>
                  <Badge variant={exam.settings?.show_results_immediately ? "default" : "secondary"}>
                    {exam.settings?.show_results_immediately ? "Oui" : "Non"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Révision autorisée</span>
                  <Badge variant={exam.settings?.allow_review ? "default" : "secondary"}>
                    {exam.settings?.allow_review ? "Oui" : "Non"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Surveillance anti-triche</span>
                  <Badge variant={exam.settings?.enable_proctoring ? "default" : "secondary"}>
                    <Shield className="h-3 w-3 mr-1" />
                    {exam.settings?.enable_proctoring ? "Activée" : "Désactivée"}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Questions List */}
        {exam.questions && exam.questions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Questions ({exam.questions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {exam.questions.map((q, index) => (
                  <div key={q.id} className="flex items-start gap-4 p-3 bg-muted rounded-lg">
                    <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{q.question_text?.replace(/<[^>]*>/g, '')}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {typeLabels[q.type] || q.type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {q.points || 1} pt{(q.points || 1) > 1 ? 's' : ''}
                        </span>
                        {q.difficulty && (
                          <Badge variant="secondary" className="text-xs">
                            {q.difficulty === 'easy' ? 'Facile' : q.difficulty === 'hard' ? 'Difficile' : 'Moyen'}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Quick Actions */}
        {canManage && (
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}/stats`)}>
              <BarChart3 className="h-4 w-4 mr-2" />
              Voir les statistiques
            </Button>
            <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams/${examId}/attempts`)}>
              <Users className="h-4 w-4 mr-2" />
              Voir les tentatives
            </Button>
          </div>
        )}

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-green-600" />
                Partager l'examen
              </DialogTitle>
              <DialogDescription>
                Partagez ce lien avec les candidats pour qu'ils puissent s'inscrire et passer l'examen.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Lien d'inscription public</label>
                <div className="flex gap-2">
                  <Input 
                    value={getShareUrl()} 
                    readOnly 
                    className="font-mono text-sm"
                  />
                  <Button onClick={copyShareLink}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
                  <Link2 className="h-4 w-4" />
                  Comment ça marche ?
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>1. Les candidats accèdent au lien</li>
                  <li>2. Ils remplissent un formulaire d'inscription simple</li>
                  <li>3. Ils passent directement l'examen</li>
                  <li>4. Vous retrouvez leurs résultats dans les tentatives</li>
                </ul>
              </div>
              
              <div className="flex justify-between items-center pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => window.open(getShareUrl(), '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Aperçu
                </Button>
                <Button onClick={() => setShareDialogOpen(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </WorkspaceLayout>
  );
}
